import { useEffect, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Info,
  Receipt,
  PieChart as PieChartIcon,
  Loader2,
} from "lucide-react";
import api from "../../lib/api";

const fmtEUR = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });
};
const fmtPctSigned = (v) => {
  const n = Number(v) || 0;
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(1)} pts`;
};
const fmtPct = (v) => {
  const n = Number(v) || 0;
  return `${n.toFixed(1)}%`;
};

const CATEGORY_LABELS = {
  purchases: "Compras / mat. prima",
  utilities: "Suministros",
  payroll: "Nóminas",
  rent: "Alquiler",
  cleaning: "Limpieza",
  marketing: "Marketing",
  professional: "Servicios prof.",
  other: "Otros",
};

/**
 * KPI delta row — shows current value, projected value and the diff with
 * a directional arrow + semantic color (green = improves margin, red =
 * worsens it).
 */
function DeltaRow({ label, before, after, unit = "€", invertedSign = false, testId }) {
  const delta = (Number(after) || 0) - (Number(before) || 0);
  const sign = Math.abs(delta) < 0.01 ? "neutral" : delta > 0 ? "up" : "down";
  // For costs: up = bad (red); for margin: up = good (green). `invertedSign`
  // flips the semantic meaning.
  const isGood = invertedSign ? sign === "up" : sign === "down";
  const isBad  = invertedSign ? sign === "down" : sign === "up";
  const Icon = sign === "up" ? TrendingUp : sign === "down" ? TrendingDown : Minus;
  const colorCls =
    sign === "neutral" ? "text-muted-foreground" :
    isGood ? "text-success" : isBad ? "text-destructive" : "text-muted-foreground";

  const fmt = (v) => unit === "€" ? fmtEUR(v) : fmtPct(v);
  const fmtDelta = unit === "€"
    ? (delta > 0 ? "+" : "") + fmtEUR(delta)
    : fmtPctSigned(delta);

  return (
    <div className="flex items-center justify-between gap-3" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground tabular-nums">{fmt(before)}</span>
        <span className="text-muted-foreground/40">→</span>
        <span className="font-semibold text-foreground tabular-nums">{fmt(after)}</span>
        <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium tabular-nums ${colorCls}`}>
          <Icon className="w-3 h-3" />
          {fmtDelta}
        </span>
      </div>
    </div>
  );
}

/**
 * InvoiceImpactPanel — shows a snapshot of how this invoice will affect the
 * P&L of the current month, helping the user decide before confirming.
 *
 * Designed to live inside DocumentReviewDialog as a collapsible section.
 */
export function InvoiceImpactPanel({ restaurantId, documentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.getDocumentImpact(restaurantId, documentId);
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(err?.message || "No se pudo calcular el impacto");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId, documentId]);

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="invoice-impact-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Calculando impacto…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-border bg-muted/40 p-3 flex items-start gap-2 text-xs text-muted-foreground" data-testid="invoice-impact-error">
        <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <span>{error || "No se puede mostrar el impacto en este momento."}</span>
      </div>
    );
  }

  const { invoice, month_so_far: before, month_after: after, deltas, top_categories: topCats } = data;
  const noRevenue = !before.revenue || before.revenue <= 0;
  const catLabel = CATEGORY_LABELS[invoice.category] || invoice.category;

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="invoice-impact-panel">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-primary/5">
        <PieChartIcon className="w-4 h-4 text-primary" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-foreground">Impacto en el mes</div>
          <div className="text-[11px] text-muted-foreground">
            Cómo afectará esta factura al P&amp;L de {data.month_window.from?.slice(0, 7)} cuando la confirmes.
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Invoice summary */}
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-lg bg-muted/40 p-2.5" data-testid="impact-invoice-subtotal">
            <div className="text-[10px] uppercase text-muted-foreground tracking-wide">Base</div>
            <div className="text-base font-semibold text-foreground tabular-nums">{fmtEUR(invoice.subtotal)}</div>
          </div>
          <div className="rounded-lg bg-muted/40 p-2.5" data-testid="impact-invoice-tax">
            <div className="text-[10px] uppercase text-muted-foreground tracking-wide">IVA</div>
            <div className="text-base font-semibold text-foreground tabular-nums">{fmtEUR(invoice.tax_amount)}</div>
          </div>
          <div className="rounded-lg bg-primary/10 border border-primary/20 p-2.5" data-testid="impact-invoice-total">
            <div className="text-[10px] uppercase text-primary/80 tracking-wide font-medium">Total</div>
            <div className="text-base font-semibold text-primary tabular-nums">{fmtEUR(invoice.total)}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <Receipt className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">
            Categoría: <span className="text-foreground font-medium">{catLabel}</span>
            {invoice.is_purchase && <> · afecta al food-cost</>}
          </span>
        </div>

        {/* Delta rows */}
        <div className="space-y-2.5">
          <DeltaRow
            label="Gastos del mes"
            before={before.expenses_total}
            after={after.expenses_total}
            unit="€"
            testId="impact-delta-expenses"
          />
          {invoice.is_purchase && (
            <DeltaRow
              label="Food-cost %"
              before={before.food_cost_pct}
              after={after.food_cost_pct}
              unit="%"
              testId="impact-delta-foodcost"
            />
          )}
          <DeltaRow
            label="Margen neto %"
            before={before.net_margin_pct}
            after={after.net_margin_pct}
            unit="%"
            invertedSign
            testId="impact-delta-margin"
          />
          <DeltaRow
            label="Beneficio neto"
            before={before.net_profit}
            after={after.net_profit}
            unit="€"
            invertedSign
            testId="impact-delta-net-profit"
          />
        </div>

        {noRevenue && (
          <div className="flex items-start gap-2 rounded-lg bg-warning/10 border border-warning/30 px-3 py-2 text-xs" data-testid="impact-no-revenue">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-warning flex-shrink-0" />
            <span className="text-foreground/80">
              Aún no hay ventas registradas este mes, así que los % de margen y food-cost se mostrarán cuando empiecen a entrar tickets.
            </span>
          </div>
        )}

        {/* Category share */}
        <div className="rounded-lg bg-muted/30 px-3 py-2" data-testid="impact-category-share">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Peso de esta factura sobre los gastos del mes</span>
            <span className="font-semibold text-foreground tabular-nums">{fmtPct(deltas.category_share_pct)}</span>
          </div>
          <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${Math.min(100, Math.max(2, deltas.category_share_pct))}%` }}
            />
          </div>
        </div>

        {/* Top categories context */}
        {topCats && topCats.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-[11px] uppercase text-muted-foreground tracking-wide font-medium">
              Top gastos del mes (sin esta factura)
            </div>
            <div className="space-y-1">
              {topCats.map((c) => (
                <div key={c.category} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {CATEGORY_LABELS[c.category] || c.category}
                  </span>
                  <span className="font-medium text-foreground tabular-nums">{fmtEUR(c.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InvoiceImpactPanel;
